import React from "react";
import PageHeader from "../../common/PageHeader";
import CommonDialog from "../../common/CommonDialog";
import { GlobalContext } from "../../context/GlobalContext";
import {
  PersonSearchOutlined,
  AddOutlined,
  OpenInNewOutlined,
  ContentCopyOutlined,
  EditOutlined,
  GroupsOutlined,
  StyleOutlined,
  Star,
  StarBorder,
  Close as CloseIcon,
} from "@mui/icons-material";
import {
  Alert,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Select,
  Skeleton,
  Snackbar,
  Stack,
  Tab,
  Tabs,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";

/** URL string safe for <img>/<video> preview (http(s), blob, data, protocol-relative). */
function previewablePublicAssetUrl(raw) {
  const t = String(raw ?? "").trim();
  if (!t) return null;
  if (/^(https?:|blob:|data:)/i.test(t)) return t;
  if (/^\/\//.test(t)) return `https:${t}`;
  if (/^[a-z0-9][\w.-]+\.[a-z]{2,}(\/.*)?$/i.test(t)) return `https://${t}`;
  return null;
}

/** Keep faces visible when `object-fit: cover` crops a portrait into a fixed frame. */
const previewMediaSx = {
  width: "100%",
  height: "100%",
  objectFit: "cover",
  objectPosition: "center top",
  display: "block",
};

/** Hidden from Actors UI (product decision / clutter). */
const EXCLUDED_ACTOR_NAMES = new Set(["Brandon_Business_Sitting_Side_public"]);

function filterActorGroup(g) {
  if (!g || typeof g !== "object") return false;
  if (EXCLUDED_ACTOR_NAMES.has(String(g.name || ""))) return false;
  return true;
}

function filterActorLook(lk) {
  if (!lk || typeof lk !== "object") return false;
  if (lk.avatar_type === "studio_avatar") return false;
  if (EXCLUDED_ACTOR_NAMES.has(String(lk.name || ""))) return false;
  return true;
}

/** Looks inside a single avatar group — keep studio avatars so the modal matches the dashboard. */
function filterActorLookInGroupDialog(lk) {
  if (!lk || typeof lk !== "object") return false;
  if (EXCLUDED_ACTOR_NAMES.has(String(lk.name || ""))) return false;
  return true;
}

function firstNonEmptyString(...vals) {
  for (const v of vals) {
    if (typeof v === "string" && v.trim()) return v.trim();
  }
  return null;
}

function pickLookPreviewImage(lk) {
  if (!lk || typeof lk !== "object") return null;
  return firstNonEmptyString(
    lk.preview_image_url,
    lk.preview_image,
    lk.previewImageUrl,
    lk.image_url,
    lk.photo_url,
    lk.thumbnail_url,
    lk.cover_image_url,
    lk?.preview?.image_url,
    lk?.preview?.url,
  );
}

function pickLookPreviewVideo(lk) {
  if (!lk || typeof lk !== "object") return null;
  return firstNonEmptyString(
    lk.preview_video_url,
    lk.preview_video,
    lk.previewVideoUrl,
    lk.video_url,
    lk?.preview?.video_url,
  );
}

function pickGroupPreviewImage(g) {
  if (!g || typeof g !== "object") return null;
  return firstNonEmptyString(
    g.preview_image_url,
    g.preview_image,
    g.previewImageUrl,
    g.image_url,
    g.thumbnail_url,
    g.cover_image_url,
  );
}

function pickGroupPreviewVideo(g) {
  if (!g || typeof g !== "object") return null;
  return firstNonEmptyString(
    g.preview_video_url,
    g.preview_video,
    g.previewVideoUrl,
    g.video_url,
  );
}

const FAV_GROUPS_KEY = "heygen_actors_fav_groups";
const FAV_LOOKS_KEY = "heygen_actors_fav_looks";

function readFavIds(key) {
  try {
    const raw = localStorage.getItem(key);
    const arr = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(arr) ? arr.filter(Boolean) : []);
  } catch {
    return new Set();
  }
}

function lookHaystack(lk) {
  const tags = Array.isArray(lk.tags) ? lk.tags.map((t) => String(t).toLowerCase()).join(" ") : "";
  return `${String(lk.name || "").toLowerCase()} ${tags}`;
}

/** HeyGen only documents `ownership` on list APIs; other shelves use tag + name heuristics. */
function matchesStyleCategoryLook(lk, cat) {
  if (cat === "all" || cat === "community" || cat === "favourites") return true;
  const h = lookHaystack(lk);
  if (cat === "professional") {
    return /professional|business|formal|corporate|executive|suit|interview|office|brand|presenter/.test(h);
  }
  if (cat === "lifestyle") {
    return /lifestyle|outdoor|home|street|travel|everyday|coffee|park|natural|living/.test(h);
  }
  if (cat === "ugc") {
    return /ugc|authentic|selfie|social|creator|phone|tiktok|reels|raw|teaser|casual/.test(h);
  }
  return true;
}

function matchesStyleCategoryGroup(g, cat) {
  if (cat === "all" || cat === "community" || cat === "favourites") return true;
  const h = String(g.name || "").toLowerCase();
  if (cat === "professional") {
    return /professional|business|formal|corporate|executive|suit|office|brand|presenter/.test(h);
  }
  if (cat === "lifestyle") {
    return /lifestyle|outdoor|travel|everyday|natural|living|street|home/.test(h);
  }
  if (cat === "ugc") {
    return /ugc|authentic|selfie|social|creator|casual|tiktok|reels|raw/.test(h);
  }
  return true;
}

function statusColor(st) {
  const s = String(st || "").toLowerCase();
  if (s === "completed") return "success";
  if (s === "failed") return "error";
  if (s === "processing" || s === "pending_consent") return "warning";
  return "default";
}

export default function HeyGenActors({ lang }) {
  const { hitAxios } = React.useContext(GlobalContext);

  const [tab, setTab] = React.useState(0);
  const [categoryFilter, setCategoryFilter] = React.useState("all");
  const [groups, setGroups] = React.useState([]);
  const [groupToken, setGroupToken] = React.useState(null);
  const [groupHasMore, setGroupHasMore] = React.useState(false);
  const [loadingGroups, setLoadingGroups] = React.useState(true);

  const [looks, setLooks] = React.useState([]);
  const [lookToken, setLookToken] = React.useState(null);
  const [lookHasMore, setLookHasMore] = React.useState(false);
  const [loadingLooks, setLoadingLooks] = React.useState(false);

  const [favGroupIds, setFavGroupIds] = React.useState(() => readFavIds(FAV_GROUPS_KEY));
  const [favLookIds, setFavLookIds] = React.useState(() => readFavIds(FAV_LOOKS_KEY));

  const [snack, setSnack] = React.useState({ open: false, message: "", severity: "success" });
  const showSnack = (message, severity = "success") => setSnack({ open: true, message, severity });

  const [looksDialog, setLooksDialog] = React.useState({ open: false, groupId: "", title: "" });
  const [dialogLooks, setDialogLooks] = React.useState([]);
  const [dialogLookToken, setDialogLookToken] = React.useState(null);
  const [dialogLookHasMore, setDialogLookHasMore] = React.useState(false);
  const [loadingDialogLooks, setLoadingDialogLooks] = React.useState(false);

  const [createOpen, setCreateOpen] = React.useState(false);
  const [createType, setCreateType] = React.useState("photo");
  const [createName, setCreateName] = React.useState("");
  const [createAssetUrl, setCreateAssetUrl] = React.useState("");
  const [createAssetFile, setCreateAssetFile] = React.useState(null);
  const [createAssetObjectUrl, setCreateAssetObjectUrl] = React.useState(null);
  const assetFileInputRef = React.useRef(null);
  const [createPrompt, setCreatePrompt] = React.useState("");
  const [createRefUrls, setCreateRefUrls] = React.useState("");
  const [createBusy, setCreateBusy] = React.useState(false);
  const [createAssetInputMode, setCreateAssetInputMode] = React.useState("url");

  const resetCreateForm = React.useCallback(() => {
    setCreateName("");
    setCreateAssetUrl("");
    setCreateAssetFile(null);
    setCreatePrompt("");
    setCreateRefUrls("");
    setCreateAssetInputMode("url");
  }, []);

  React.useEffect(() => {
    if (!createAssetFile) {
      setCreateAssetObjectUrl(null);
      return undefined;
    }
    const u = URL.createObjectURL(createAssetFile);
    setCreateAssetObjectUrl(u);
    return () => URL.revokeObjectURL(u);
  }, [createAssetFile]);

  React.useEffect(() => {
    setCreateAssetFile(null);
    setCreateAssetUrl("");
    setCreateAssetInputMode("url");
  }, [createType]);

  const [renameOpen, setRenameOpen] = React.useState(false);
  const [renameLook, setRenameLook] = React.useState(null);
  const [renameValue, setRenameValue] = React.useState("");
  const [renameBusy, setRenameBusy] = React.useState(false);

  const [lookLightbox, setLookLightbox] = React.useState({
    open: false,
    src: null,
    title: "",
    mode: "image",
  });

  const toggleFavGroup = (id, e) => {
    e?.stopPropagation?.();
    setFavGroupIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      localStorage.setItem(FAV_GROUPS_KEY, JSON.stringify([...n]));
      return n;
    });
    queueMicrotask(() => {
      if (categoryFilter === "favourites" && tab === 0) fetchGroups(false);
    });
  };

  const toggleFavLook = (id, e) => {
    e?.stopPropagation?.();
    setFavLookIds((prev) => {
      const n = new Set(prev);
      if (n.has(id)) n.delete(id);
      else n.add(id);
      localStorage.setItem(FAV_LOOKS_KEY, JSON.stringify([...n]));
      return n;
    });
    queueMicrotask(() => {
      if (categoryFilter === "favourites" && tab === 1) fetchLooksLibrary(false);
    });
  };

  const fetchGroups = async (append) => {
    setLoadingGroups(true);
    if (!append) setGroups([]);
    const q = new URLSearchParams();
    if (categoryFilter === "community") q.set("ownership", "public");
    q.set("limit", "24");
    if (append && groupToken) q.set("token", groupToken);
    const qs = q.toString();
    const res = await hitAxios({
      path: `/api/heygen/v3/avatar-groups?${qs}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoadingGroups(false);
    if (res?.data?.success) {
      const raw = (res.data.data?.groups || []).filter(filterActorGroup);
      const next = raw.filter((g) => {
        if (categoryFilter === "favourites") return readFavIds(FAV_GROUPS_KEY).has(g.id);
        return matchesStyleCategoryGroup(g, categoryFilter);
      });
      setGroups((prev) => (append ? [...prev, ...next] : next));
      setGroupHasMore(Boolean(res.data.data?.has_more));
      setGroupToken(res.data.data?.next_token || null);
    } else {
      if (!append) setGroups([]);
      showSnack(res?.data?.msg || lang?.actorsLoadError || "Could not load avatar groups.", "error");
    }
  };

  React.useEffect(() => {
    setGroupToken(null);
    fetchGroups(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- reset list when catalog filter changes
  }, [categoryFilter]);

  const fetchLooksLibrary = async (append) => {
    setLoadingLooks(true);
    if (!append) setLooks([]);
    const q = new URLSearchParams();
    if (categoryFilter === "community") q.set("ownership", "public");
    q.set("limit", "24");
    if (append && lookToken) q.set("token", lookToken);
    const res = await hitAxios({
      path: `/api/heygen/v3/avatar-looks?${q.toString()}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoadingLooks(false);
    if (res?.data?.success) {
      const raw = (res.data.data?.looks || []).filter(filterActorLook);
      const next = raw.filter((lk) => {
        if (categoryFilter === "favourites") return readFavIds(FAV_LOOKS_KEY).has(lk.id);
        return matchesStyleCategoryLook(lk, categoryFilter);
      });
      setLooks((prev) => (append ? [...prev, ...next] : next));
      setLookHasMore(Boolean(res.data.data?.has_more));
      setLookToken(res.data.data?.next_token || null);
    } else {
      if (!append) setLooks([]);
      showSnack(res?.data?.msg || lang?.actorsLooksError || "Could not load looks.", "error");
    }
  };

  React.useEffect(() => {
    if (tab !== 1) return;
    setLookToken(null);
    fetchLooksLibrary(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, categoryFilter]);

  const fetchDialogLooks = async (groupId, append) => {
    setLoadingDialogLooks(true);
    if (!append) setDialogLooks([]);
    const q = new URLSearchParams();
    q.set("group_id", groupId);
    q.set("limit", "30");
    if (append && dialogLookToken) q.set("token", dialogLookToken);
    const res = await hitAxios({
      path: `/api/heygen/v3/avatar-looks?${q.toString()}`,
      post: false,
      showLoading: false,
      showSnackbar: false,
    });
    setLoadingDialogLooks(false);
    if (res?.data?.success) {
      const next = (res.data.data?.looks || []).filter(filterActorLookInGroupDialog);
      setDialogLooks((prev) => (append ? [...prev, ...next] : next));
      setDialogLookHasMore(Boolean(res.data.data?.has_more));
      setDialogLookToken(res.data.data?.next_token || null);
    } else {
      if (!append) setDialogLooks([]);
      showSnack(res?.data?.msg || lang?.actorsLooksError || "Could not load looks.", "error");
    }
  };

  const openLooksForGroup = (g) => {
    setLooksDialog({ open: true, groupId: g.id, title: g.name || g.id });
    setDialogLookToken(null);
    setDialogLooks([]);
    fetchDialogLooks(g.id, false);
  };

  const requestConsent = async (groupId) => {
    const res = await hitAxios({
      path: `/api/heygen/v3/avatar-groups/${encodeURIComponent(groupId)}/consent`,
      post: true,
      obj: {},
      showLoading: true,
      showSnackbar: false,
    });
    if (res?.data?.success && res.data.data?.url) {
      window.open(String(res.data.data.url), "_blank", "noopener,noreferrer");
      showSnack(lang?.actorsConsentOpened || "Consent page opened in a new tab.", "success");
    } else {
      showSnack(res?.data?.msg || lang?.actorsConsentFailed || "Could not start consent flow.", "error");
    }
  };

  const copyText = async (text) => {
    try {
      await navigator.clipboard.writeText(text);
      showSnack(lang?.actorsCopied || "Copied to clipboard.", "success");
    } catch {
      showSnack(lang?.actorsCopyFailed || "Copy failed.", "error");
    }
  };

  const openLookLightbox = (lk) => {
    const img = pickLookPreviewImage(lk);
    const vid = pickLookPreviewVideo(lk);
    if (img) {
      setLookLightbox({ open: true, src: img, title: String(lk.name || lk.id || "").trim() || "Look", mode: "image" });
    } else if (vid) {
      setLookLightbox({ open: true, src: vid, title: String(lk.name || lk.id || "").trim() || "Look", mode: "video" });
    }
  };

  const submitCreate = async () => {
    const name = createName.trim();
    if (!name) {
      showSnack(lang?.actorsNeedName || "Enter a name.", "error");
      return;
    }
    let body;
    if (createType === "photo" || createType === "digital_twin") {
      let url;
      if (createAssetInputMode === "upload") {
        if (!createAssetFile) {
          showSnack(lang?.actorsNeedUploadFile || "Choose a file to upload.", "error");
          return;
        }
        setCreateBusy(true);
        const fd = new FormData();
        fd.append("image", createAssetFile);
        fd.append("kind", createType);
        const up = await hitAxios({
          path: "/api/gallery/upload_avatar_public_asset",
          post: true,
          obj: fd,
          showLoading: true,
          showSnackbar: false,
        });
        if (!up?.data?.success || !up.data.data?.url) {
          setCreateBusy(false);
          showSnack(up?.data?.msg || lang?.actorsUploadAssetFailed || "Could not upload asset.", "error");
          return;
        }
        url = String(up.data.data.url).trim();
      } else {
        const trimmed = createAssetUrl.trim();
        url = previewablePublicAssetUrl(trimmed) || trimmed;
      }
      if (!url) {
        showSnack(
          createAssetInputMode === "upload"
            ? lang?.actorsNeedUploadFile || "Choose a file to upload."
            : lang?.actorsNeedAssetUrl || "Enter a public asset URL.",
          "error",
        );
        return;
      }
      if (!/^https?:\/\//i.test(url)) {
        if (createAssetInputMode === "upload") setCreateBusy(false);
        showSnack(lang?.actorsNeedHttpsUrl || "Asset must resolve to a public http(s) URL the video service can fetch.", "error");
        return;
      }
      body = {
        type: createType,
        name,
        file: { type: "url", url },
      };
    } else {
      const prompt = createPrompt.trim();
      if (!prompt) {
        showSnack(lang?.actorsNeedPrompt || "Enter a prompt.", "error");
        return;
      }
      body = { type: "prompt", name, prompt };
      const refs = createRefUrls
        .split(/[\n,]+/)
        .map((s) => s.trim())
        .filter(Boolean)
        .slice(0, 3);
      if (refs.length) {
        body.reference_images = refs.map((u) => ({ type: "url", url: u }));
      }
    }

    setCreateBusy(true);
    const res = await hitAxios({
      path: "/api/heygen/v3/avatars",
      post: true,
      obj: body,
      showLoading: true,
      showSnackbar: false,
    });
    setCreateBusy(false);
    if (res?.data?.success) {
      showSnack(lang?.actorsCreateStarted || "Avatar creation started. Training may take several minutes.", "success");
      setCreateOpen(false);
      resetCreateForm();
      fetchGroups(false);
    } else {
      showSnack(res?.data?.msg || lang?.actorsCreateFailed || "Create request failed.", "error");
    }
  };

  const submitRename = async () => {
    if (!renameLook?.id) return;
    const name = renameValue.trim();
    if (!name) {
      showSnack(lang?.actorsNeedName || "Enter a name.", "error");
      return;
    }
    setRenameBusy(true);
    const res = await hitAxios({
      path: `/api/heygen/v3/avatar-looks/${encodeURIComponent(renameLook.id)}`,
      method: "PATCH",
      post: false,
      obj: { name },
      showLoading: true,
      showSnackbar: false,
    });
    setRenameBusy(false);
    if (res?.data?.success) {
      showSnack(lang?.actorsRenamed || "Look updated.", "success");
      setRenameOpen(false);
      setRenameLook(null);
      if (looksDialog.open) fetchDialogLooks(looksDialog.groupId, false);
      if (tab === 1) fetchLooksLibrary(false);
    } else {
      showSnack(res?.data?.msg || lang?.actorsRenameFailed || "Rename failed.", "error");
    }
  };

  const renderGroupCard = (g) => {
    const img = pickGroupPreviewImage(g);
    const vid = pickGroupPreviewVideo(g);
    const hasImg = !!img;
    const hasVid = !!vid;
    const isFav = favGroupIds.has(g.id);
    return (
      <Grid item xs={12} sm={6} md={4} lg={3} key={g.id}>
        <Card
          onClick={() => openLooksForGroup(g)}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              e.preventDefault();
              openLooksForGroup(g);
            }
          }}
          sx={{
            borderRadius: 2,
            overflow: "hidden",
            border: 1,
            borderColor: "divider",
            height: 320,
            display: "flex",
            flexDirection: "column",
            cursor: "pointer",
            transition: "transform 0.2s, border-color 0.2s",
            "&:hover": { transform: "translateY(-4px)", borderColor: "primary.main" },
          }}
        >
          <Box sx={{ position: "relative", flex: 1, minHeight: 0, overflow: "hidden" }}>
            {hasImg ? (
              <Box
                component="img"
                src={img}
                alt=""
                referrerPolicy="no-referrer"
                sx={{ ...previewMediaSx }}
              />
            ) : hasVid ? (
              <Box
                component="video"
                src={vid}
                muted
                loop
                playsInline
                autoPlay
                sx={{ ...previewMediaSx }}
              />
            ) : (
              <Skeleton variant="rectangular" height="100%" />
            )}
            <IconButton
              size="small"
              onClick={(e) => toggleFavGroup(g.id, e)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.45)",
                color: "warning.light",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
              aria-label={lang?.actorsFavourite || "Favourite"}
            >
              {isFav ? <Star sx={{ fontSize: 20 }} /> : <StarBorder sx={{ fontSize: 20 }} />}
            </IconButton>
            <Stack direction="row" spacing={0.5} sx={{ position: "absolute", top: 8, left: 8, flexWrap: "wrap", maxWidth: "calc(100% - 48px)" }}>
              {g.status ? <Chip size="small" label={g.status} color={statusColor(g.status)} /> : null}
              {g.consent_status ? (
                <Chip size="small" variant="outlined" label={`consent: ${g.consent_status}`} />
              ) : null}
            </Stack>
          </Box>
          <Box sx={{ p: 1.5, borderTop: 1, borderColor: "divider" }}>
            <Typography fontWeight={700} noWrap>
              {g.name}
            </Typography>
            <Typography variant="caption" color="text.secondary" display="block" noWrap>
              {(lang?.actorsLooksCount || "{n} looks").replace("{n}", String(g.looks_count ?? "—"))}
              {g.gender ? ` · ${g.gender}` : ""}
            </Typography>
            <Stack direction="row" spacing={0.5} sx={{ mt: 1 }} flexWrap="wrap" useFlexGap>
              <Button
                size="small"
                variant="text"
                onClick={(e) => {
                  e.stopPropagation();
                  requestConsent(g.id);
                }}
                sx={{ textTransform: "none" }}
              >
                {lang?.actorsConsent || "Consent"}
              </Button>
            </Stack>
          </Box>
        </Card>
      </Grid>
    );
  };

  const renderLookCard = (lk, dense) => {
    const img = pickLookPreviewImage(lk);
    const vid = pickLookPreviewVideo(lk);
    const hasPreview = !!(img || vid);
    const canRename = lk.avatar_type === "photo_avatar" || lk.avatar_type === "digital_twin";
    const isFav = favLookIds.has(lk.id);
    return (
      <Grid item xs={12} sm={dense ? 6 : 4} md={dense ? 4 : 3} key={lk.id}>
        <Card
          onClick={() => hasPreview && openLookLightbox(lk)}
          sx={{
            borderRadius: 2,
            border: 1,
            borderColor: "divider",
            overflow: "hidden",
            cursor: hasPreview ? "pointer" : "default",
          }}
        >
          <Box sx={{ height: dense ? 160 : 200, bgcolor: "grey.200", position: "relative", overflow: "hidden" }}>
            {img ? (
              <Box
                component="img"
                src={img}
                alt=""
                referrerPolicy="no-referrer"
                sx={{ ...previewMediaSx }}
              />
            ) : vid ? (
              <Box
                component="video"
                src={vid}
                muted
                loop
                playsInline
                sx={{ ...previewMediaSx, pointerEvents: "none" }}
              />
            ) : (
              <Skeleton variant="rectangular" height="100%" />
            )}
            <IconButton
              size="small"
              onClick={(e) => toggleFavLook(lk.id, e)}
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                bgcolor: "rgba(0,0,0,0.45)",
                color: "warning.light",
                "&:hover": { bgcolor: "rgba(0,0,0,0.6)" },
              }}
              aria-label={lang?.actorsFavourite || "Favourite"}
            >
              {isFav ? <Star sx={{ fontSize: 20 }} /> : <StarBorder sx={{ fontSize: 20 }} />}
            </IconButton>
            <Chip
              size="small"
              label={lk.avatar_type || "look"}
              sx={{ position: "absolute", bottom: 8, left: 8 }}
              color="primary"
              variant="outlined"
            />
          </Box>
          <Box sx={{ p: 1.25 }}>
            <Typography fontWeight={700} variant="body2" noWrap>
              {lk.name}
            </Typography>
            <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={0.5} sx={{ mt: 0.5 }}>
              <Tooltip title={lang?.actorsCopyId || "Copy look id"}>
                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    copyText(lk.id);
                  }}
                >
                  <ContentCopyOutlined sx={{ fontSize: 16 }} />
                </IconButton>
              </Tooltip>
              {canRename ? (
                <Tooltip title={lang?.actorsRename || "Rename"}>
                  <IconButton
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      setRenameLook(lk);
                      setRenameValue(lk.name || "");
                      setRenameOpen(true);
                    }}
                  >
                    <EditOutlined sx={{ fontSize: 16 }} />
                  </IconButton>
                </Tooltip>
              ) : null}
            </Stack>
            {lk.status ? (
              <Chip sx={{ mt: 0.5 }} size="small" label={lk.status} color={statusColor(lk.status)} />
            ) : null}
          </Box>
        </Card>
      </Grid>
    );
  };

  return (
    <div>
      <PageHeader
        icon={PersonSearchOutlined}
        title={lang?.actorsTitle || "Actors"}
        subtitle={
          lang?.actorsSubtitle ||
          "Browse  actor groups and looks from the. Use look ids as avatar_id when generating videos."
        }
        primaryAction={
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
            <Button
              variant="contained"
              size="large"
              startIcon={<AddOutlined />}
              onClick={() => {
                resetCreateForm();
                setCreateType("photo");
                setCreateOpen(true);
              }}
              sx={{ fontWeight: 700 }}
            >
              {lang?.actorsCreate || "Create avatar"}
            </Button>
          </Stack>
        }
      />

      <PaperLikeTabs tab={tab} setTab={setTab} lang={lang} />

      <Box sx={{ mb: 2 }}>
        <CatalogFilterChips value={categoryFilter} onChange={setCategoryFilter} lang={lang} />
        <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 0.75 }}>
          {lang?.actorsFilterHint ||
            "Use the public catalog."}
        </Typography>
      </Box>

      {tab === 0 && (
        <Box>
          {loadingGroups && !groups.length ? (
            <Grid container spacing={2}>
              {[1, 2, 3, 4].map((i) => (
                <Grid item xs={12} sm={6} md={4} key={i}>
                  <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                </Grid>
              ))}
            </Grid>
          ) : (
            <>
              <Grid container spacing={2}>
                {groups.map((g) => renderGroupCard(g))}
              </Grid>
              {groupHasMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button variant="outlined" disabled={loadingGroups} onClick={() => fetchGroups(true)} sx={{ textTransform: "none" }}>
                    {loadingGroups ? <CircularProgress size={22} /> : lang?.actorsLoadMore || "Load more"}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      )}

      {tab === 1 && (
        <Box>
          {loadingLooks && !looks.length ? (
            <CircularProgress />
          ) : (
            <>
              <Grid container spacing={2}>
                {looks.map((lk) => renderLookCard(lk, false))}
              </Grid>
              {lookHasMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button variant="outlined" disabled={loadingLooks} onClick={() => fetchLooksLibrary(true)} sx={{ textTransform: "none" }}>
                    {loadingLooks ? <CircularProgress size={22} /> : lang?.actorsLoadMore || "Load more"}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      )}

      <CommonDialog
        open={looksDialog.open}
        onClose={() => setLooksDialog((d) => ({ ...d, open: false }))}
        title={(lang?.actorsLooksTitle || "Looks — {name}").replace("{name}", looksDialog.title)}
        subtitle={lang?.actorsLooksSubtitle || "Look id is the avatar_id for POST /v3/videos."}
        icon={StyleOutlined}
        maxWidth="lg"
      >
        <Box sx={{ py: 1 }}>
          {loadingDialogLooks && !dialogLooks.length ? (
            <CircularProgress />
          ) : (
            <>
              <Grid container spacing={2}>
                {dialogLooks.map((lk) => renderLookCard(lk, true))}
              </Grid>
              {dialogLookHasMore ? (
                <Box sx={{ display: "flex", justifyContent: "center", mt: 2 }}>
                  <Button
                    variant="outlined"
                    disabled={loadingDialogLooks}
                    onClick={() => fetchDialogLooks(looksDialog.groupId, true)}
                    sx={{ textTransform: "none" }}
                  >
                    {loadingDialogLooks ? <CircularProgress size={22} /> : lang?.actorsLoadMore || "Load more"}
                  </Button>
                </Box>
              ) : null}
            </>
          )}
        </Box>
      </CommonDialog>

      <CommonDialog
        open={createOpen}
        onClose={() => {
          if (createBusy) return;
          setCreateOpen(false);
          resetCreateForm();
        }}
        title={lang?.actorsCreateTitle || "Create avatar"}
        subtitle={lang?.actorsCreateSubtitle || "Training is asynchronous. Use a public HTTPS URL or upload an asset."}
        icon={AddOutlined}
        maxWidth="sm"
        contentSx={{ pt: 6, pb: 4 }}
      >
        <Stack spacing={2.5} sx={{ pt: 2.5 }}>
          <FormControl fullWidth size="small">
            <InputLabel id="actors-create-type-label">{lang?.actorsCreateType || "Type"}</InputLabel>
            <Select
              labelId="actors-create-type-label"
              label={lang?.actorsCreateType || "Type"}
              value={createType}
              onChange={(e) => setCreateType(e.target.value)}
              renderValue={(v) =>
                v === "photo"
                  ? lang?.actorsCreateTypePhoto || "Photo"
                  : v === "digital_twin"
                    ? lang?.actorsCreateTypeDigitalTwin || "Digital Twin"
                    : lang?.actorsCreateTypePrompt || "Prompt"
              }
            >
              <MenuItem value="photo">{lang?.actorsCreateTypePhoto || "Photo"}</MenuItem>
              <MenuItem value="digital_twin">{lang?.actorsCreateTypeDigitalTwin || "Digital Twin"}</MenuItem>
              <MenuItem value="prompt">{lang?.actorsCreateTypePrompt || "Prompt"}</MenuItem>
            </Select>
          </FormControl>
          <TextField label={lang?.actorsName || "Name"} value={createName} onChange={(e) => setCreateName(e.target.value)} fullWidth required />
          {(createType === "photo" || createType === "digital_twin") && (
            <>
              <Box>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1, fontWeight: 600 }}>
                  {lang?.actorsPublicAssetLabel || "Public asset"}
                </Typography>
                <ToggleButtonGroup
                  exclusive
                  fullWidth
                  size="small"
                  value={createAssetInputMode}
                  onChange={(_, next) => {
                    if (next == null) return;
                    setCreateAssetInputMode(next);
                    if (next === "url") setCreateAssetFile(null);
                    else setCreateAssetUrl("");
                  }}
                  aria-label={lang?.actorsPublicAssetLabel || "Public asset source"}
                >
                  <ToggleButton value="url" sx={{ textTransform: "none", fontWeight: 600 }}>
                    {lang?.actorsAssetInputUrl || "URL"}
                  </ToggleButton>
                  <ToggleButton value="upload" sx={{ textTransform: "none", fontWeight: 600 }}>
                    {lang?.actorsAssetInputUpload || "Upload file"}
                  </ToggleButton>
                </ToggleButtonGroup>
              </Box>
              <input
                ref={assetFileInputRef}
                type="file"
                hidden
                accept={createType === "digital_twin" ? "video/mp4,video/webm,video/quicktime,video/*" : "image/jpeg,image/png,image/webp,image/*"}
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  setCreateAssetFile(f || null);
                  e.target.value = "";
                }}
              />
              {createAssetInputMode === "url" ? (
                <TextField
                  label={lang?.actorsPublicAssetUrl || "Asset URL"}
                  value={createAssetUrl}
                  onChange={(e) => setCreateAssetUrl(e.target.value)}
                  fullWidth
                  helperText={
                    createType === "digital_twin"
                      ? lang?.actorsAssetUrlHelpTwin || "HTTPS or HTTP URL to training video."
                      : lang?.actorsAssetUrlHelp || "HTTPS or HTTP URL to a portrait image."
                  }
                />
              ) : (
                <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
                  <Button variant="outlined" size="small" onClick={() => assetFileInputRef.current?.click()} sx={{ textTransform: "none" }}>
                    {createType === "digital_twin"
                      ? lang?.actorsChooseVideo || "Choose video"
                      : lang?.actorsChooseImage || "Choose image"}
                  </Button>
                  {createAssetFile ? (
                    <Button variant="text" size="small" onClick={() => setCreateAssetFile(null)} sx={{ textTransform: "none" }}>
                      {lang?.actorsClearFile || "Clear file"}
                    </Button>
                  ) : null}
                  {createAssetFile ? (
                    <Typography variant="caption" color="text.secondary">
                      {createAssetFile.name}
                    </Typography>
                  ) : (
                    <Typography variant="caption" color="text.secondary">
                      {lang?.actorsUploadPickHint || "Pick a file to preview and submit."}
                    </Typography>
                  )}
                </Stack>
              )}
              {(() => {
                const urlPreview =
                  createAssetInputMode === "url" ? previewablePublicAssetUrl(createAssetUrl) : null;
                const previewSrc = createAssetInputMode === "upload" ? createAssetObjectUrl : urlPreview;
                if (!previewSrc) return null;
                const isVideo =
                  createType === "digital_twin" &&
                  (createAssetFile?.type?.startsWith?.("video/") || /\.(mp4|webm|mov|m4v)(\?|#|$)/i.test(previewSrc));
                return (
                  <Box
                    sx={{
                      borderRadius: 2,
                      border: 1,
                      borderColor: "divider",
                      overflow: "hidden",
                      bgcolor: "grey.100",
                      maxHeight: 220,
                    }}
                  >
                    {isVideo ? (
                      <Box
                        component="video"
                        src={previewSrc}
                        controls
                        muted
                        playsInline
                        sx={{ width: "100%", maxHeight: 220, display: "block" }}
                      />
                    ) : (
                      <Box
                        component="img"
                        src={previewSrc}
                        alt=""
                        referrerPolicy="no-referrer"
                        sx={{ width: "100%", maxHeight: 220, objectFit: "contain", display: "block" }}
                      />
                    )}
                  </Box>
                );
              })()}
            </>
          )}
          {createType === "prompt" && (
            <>
              <TextField
                label={lang?.actorsPrompt || "Prompt"}
                value={createPrompt}
                onChange={(e) => setCreatePrompt(e.target.value)}
                fullWidth
                multiline
                minRows={3}
                inputProps={{ maxLength: 1000 }}
                helperText={`${createPrompt.length}/1000`}
              />
              <TextField
                label={lang?.actorsRefUrls || "Reference image URLs (optional, max 3, comma-separated)"}
                value={createRefUrls}
                onChange={(e) => setCreateRefUrls(e.target.value)}
                fullWidth
                helperText={lang?.actorsRefUrlsHelp || "Up to 3 comma- or newline-separated image URLs."}
              />
            </>
          )}
          <Button variant="contained" disabled={createBusy} onClick={submitCreate} sx={{ fontWeight: 700, textTransform: "none" }}>
            {createBusy ? <CircularProgress size={22} color="inherit" /> : lang?.actorsSubmitCreate || "Submit"}
          </Button>
        </Stack>
      </CommonDialog>

      <CommonDialog
        open={renameOpen}
        onClose={() => !renameBusy && setRenameOpen(false)}
        title={lang?.actorsRenameTitle || "Rename look"}
        subtitle={renameLook?.name || lang?.actorsRenameSubtitle || "Rename this look"}
        icon={EditOutlined}
        maxWidth="xs"
      >
        <Stack spacing={2} sx={{ py: 1 }}>
          <TextField label={lang?.actorsNewName || "New name"} value={renameValue} onChange={(e) => setRenameValue(e.target.value)} fullWidth />
          <Button variant="contained" disabled={renameBusy} onClick={submitRename} sx={{ textTransform: "none" }}>
            {renameBusy ? <CircularProgress size={22} color="inherit" /> : lang?.actorsSave || "Save"}
          </Button>
        </Stack>
      </CommonDialog>

      <Dialog
        open={lookLightbox.open}
        onClose={() => setLookLightbox((s) => ({ ...s, open: false }))}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            bgcolor: (theme) => (theme.palette.mode === "dark" ? "rgba(24,24,24,0.98)" : theme.palette.background.paper),
          },
        }}
      >
        <DialogTitle sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", pr: 1, gap: 1 }}>
          <Typography component="span" variant="h6" fontWeight={700} noWrap sx={{ flex: 1, minWidth: 0 }}>
            {lookLightbox.title}
          </Typography>
          <IconButton
            aria-label={lang?.actorsLookPreviewClose || "Close"}
            onClick={() => setLookLightbox((s) => ({ ...s, open: false }))}
            size="small"
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            p: { xs: 1, sm: 2 },
            pt: 0,
          }}
        >
          {lookLightbox.mode === "video" && lookLightbox.src ? (
            <Box
              component="video"
              src={lookLightbox.src}
              controls
              autoPlay
              playsInline
              sx={{ maxWidth: "100%", maxHeight: "min(85vh, 900px)", borderRadius: 1 }}
            />
          ) : lookLightbox.src ? (
            <Box
              component="img"
              src={lookLightbox.src}
              alt=""
              referrerPolicy="no-referrer"
              sx={{
                maxWidth: "100%",
                maxHeight: "min(85vh, 900px)",
                objectFit: "contain",
                display: "block",
                borderRadius: 1,
              }}
            />
          ) : null}
        </DialogContent>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={5000} onClose={() => setSnack((s) => ({ ...s, open: false }))} anchorOrigin={{ vertical: "top", horizontal: "center" }}>
        <Alert severity={snack.severity} onClose={() => setSnack((s) => ({ ...s, open: false }))} sx={{ width: "100%" }}>
          {snack.message}
        </Alert>
      </Snackbar>
    </div>
  );
}

function CatalogFilterChips({ value, onChange, lang }) {
  const items = [
    { id: "all", label: lang?.actorsFilterAll || "All" },
    { id: "professional", label: lang?.actorsFilterProfessional || "Professional" },
    { id: "lifestyle", label: lang?.actorsFilterLifestyle || "Lifestyle" },
    { id: "ugc", label: lang?.actorsFilterUgc || "UGC" },
    { id: "community", label: lang?.actorsFilterCommunity || "Community" },
    { id: "favourites", label: lang?.actorsFilterFavourites || "Favourites" },
  ];
  return (
    <Stack direction="row" spacing={1} useFlexGap flexWrap="wrap" sx={{ gap: 1 }}>
      {items.map((it) => (
        <Chip
          key={it.id}
          label={it.label}
          onClick={() => onChange(it.id)}
          color={value === it.id ? "primary" : "default"}
          variant={value === it.id ? "filled" : "outlined"}
          sx={{ fontWeight: 700 }}
        />
      ))}
    </Stack>
  );
}

function PaperLikeTabs({ tab, setTab, lang }) {
  return (
    <Box
      sx={{
        mb: 2,
        borderRadius: 2,
        border: 1,
        borderColor: "divider",
        bgcolor: "background.paper",
        px: 1,
      }}
    >
      <Tabs value={tab} onChange={(_, v) => setTab(v)} variant="scrollable" scrollButtons="auto" sx={{ "& .MuiTab-root": { textTransform: "none", fontWeight: 700 } }}>
        <Tab icon={<GroupsOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label={lang?.actorsTabGroups || "Avatar groups"} />
        <Tab icon={<StyleOutlined sx={{ fontSize: 20 }} />} iconPosition="start" label={lang?.actorsTabLooks || "Looks"} />
      </Tabs>
    </Box>
  );
}
